<template>
  <div class="z-10">
    <Button
      outlined
      data-testid="txn-area-button"
      @click="areaFilterPopover.toggle($event)"
      :label="$t(`messages.transactions.pindala`)"
      :class="{ 'p-button-primary': isDirty }"
    />
    <div class="fixed -translate-x-1/3 top-36 w-full max-w-sm">
      <Popover ref="areaFilterPopover">
        <form class="flex flex-col gap-y-6 items-center justify-center w-72">
          <div class="flex flex-col items-center text-sm gap-y-2">
            <span>{{ $t(`messages.transactions.fields.korteri_pind`) }} (m²)</span>

            <Slider
              v-model="apartmentArea"
              :min="sliderLimits.korteri_pind[0]"
              :max="sliderLimits.korteri_pind[1]"
              :step="5"
              range
              class="w-64"
              data-testid="area-apartment-slider"
              @change="updateValue()"
            />

            <div class="flex gap-2 w-64">
              <InputNumber
                v-model="apartmentFrom"
                :placeholder="$t(`messages.transactions.from`)"
                inputmode="decimal"
                :max-fraction-digits="1"
                size="small"
                :min="sliderLimits.korteri_pind[0]"
                :max="sliderLimits.korteri_pind[1]"
                :pt="apartmentFromPT"
                @blur="sortApartment()"
                @keydown.enter.prevent="sortApartment()"
              />
              <InputNumber
                v-model="apartmentTo"
                :placeholder="$t(`messages.transactions.to`)"
                inputmode="decimal"
                :max-fraction-digits="1"
                size="small"
                :min="sliderLimits.korteri_pind[0]"
                :max="sliderLimits.korteri_pind[1]"
                :pt="apartmentToPT"
                @blur="sortApartment()"
                @keydown.enter.prevent="sortApartment()"
              />
            </div>
          </div>

          <div class="flex flex-col items-center gap-y-2 text-sm">

            <div class="flex items-center justify-between w-64">
              <span>{{ $t(`messages.transactions.kinnistu.pindala`) }}</span>
              <SelectButton
                v-tooltip.bottom="$t(`messages.transactions.pindala_yhik`)"
                v-model="areaUnit"
                data-testid="area-unit-select"
                :options="[
                  { text: 'Ha', value: 'H' },
                  { text: 'm2', value: 'M' },
                ]"
                :allow-empty="false"
                option-label="text"
                option-value="value"
                size="small"
                :pt="{
                  pcToggleButton: {
                    content: 'py-0 px-1'
                  }
                }"
                :pt-options="{
                  mergeProps: true
                }"
                @change="unitChange"
              />
            </div>

            <Slider
              v-model="totalArea"
              :min="sliderLimits.pindala[areaUnit][0]"
              :max="sliderLimits.pindala[areaUnit][1]"
              :step="10"
              range
              class="w-64"
              data-testid="area-total-slider"
              @change="updateValue()"
            />

            <div class="flex gap-2 w-64">
              <InputNumber
                v-model="totalFrom"
                :placeholder="$t(`messages.transactions.from`)"
                inputmode="decimal"
                :max-fraction-digits="areaUnit === 'H' ? 4 : 0"
                size="small"
                :min="sliderLimits.pindala[areaUnit][0]"
                :max="sliderLimits.pindala[areaUnit][1]"
                :pt="totalFromPT"
                @blur="sortTotal()"
                @keydown.enter.prevent="sortTotal()"
              />
              <InputNumber
                v-model="totalTo"
                :placeholder="$t(`messages.transactions.to`)"
                inputmode="decimal"
                :max-fraction-digits="areaUnit === 'H' ? 4 : 0"
                size="small"
                :min="sliderLimits.pindala[areaUnit][0]"
                :max="sliderLimits.pindala[areaUnit][1]"
                :pt="totalToPT"
                @blur="sortTotal()"
                @keydown.enter.prevent="sortTotal()"
              />
            </div>
          </div>

          <div v-if="isDirty">
            <Button severity="secondary" size="small" data-testid="area-reset" @click.stop.prevent="reset">
              <span class="pi pi-times mr-1"></span> {{ $t(`messages.transactions.reset`) }}
            </Button>
          </div>
        </form>
      </Popover>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useHelpers } from '@/composables/useHelpers'
import { SEARCH_DEFAULT_UNIT, SEARCH_SLIDER_LIMITS } from '@/constants'
import { deepCopy } from '@/services/utilities'

const props = defineProps({
  apartment: {
    type: Array,
    default: () => [0, 1],
  },
  area: {
    type: Array,
    default: () => [0, 1],
  },
  unit: {
    type: String,
    default: SEARCH_DEFAULT_UNIT,
  },
})

const emit = defineEmits(['change'])

const { isFilterDirty } = useHelpers()

const areaFilterPopover = ref(null)
const apartmentArea = ref([0, 1])
const totalArea = ref([0, 1])
const areaUnit = ref(SEARCH_DEFAULT_UNIT)

// Replace old Vue 2 watchers with Vue 3 watch functions
watch(
  () => props.apartment,
  (val, old) => {
    if (val && val !== old) {
      apartmentArea.value = val
    }
  },
)

watch(
  () => props.area,
  (val, old) => {
    if (val && val !== old) {
      totalArea.value = val
    }
  },
)

watch(
  () => props.unit,
  (val, old) => {
    if (val && val !== old) {
      areaUnit.value = val
    }
  },
)

const sliderLimits = computed(() => {
  return SEARCH_SLIDER_LIMITS
})

const isDirty = computed(() => {
  return (
    isFilterDirty(apartmentArea.value, sliderLimits.value.korteri_pind) ||
    isFilterDirty(
      totalArea.value,
      sliderLimits.value.pindala[areaUnit.value],
    ) ||
    areaUnit.value !== SEARCH_DEFAULT_UNIT
  )
})

onMounted(() => {
  apartmentArea.value = props.apartment
  totalArea.value = props.area
  areaUnit.value = props.unit
})

const inputPT = (testid) => ({
  root: { class: 'w-full' },
  pcInputText: { root: { class: 'w-full', 'data-testid': testid } },
})

const apartmentFromPT = inputPT('area-apartment-from')
const apartmentToPT = inputPT('area-apartment-to')
const totalFromPT = inputPT('area-total-from')
const totalToPT = inputPT('area-total-to')

const apartmentLimits = computed(() => sliderLimits.value.korteri_pind)
const totalLimits = computed(() => sliderLimits.value.pindala[areaUnit.value])

// Writable input model over one bound of a [min, max] slider array. A bound
// sitting at its limit renders as an empty input ("no bound"); a cleared
// input restores the limit.
const boundModel = (target, limits, index) => computed({
  get: () => (target.value[index] === limits.value[index] ? null : target.value[index]),
  set: (value) => {
    const bound = value == null ? limits.value[index] : value
    if (bound !== target.value[index]) {
      const next = [...target.value]
      next[index] = bound
      target.value = next
      updateValue()
    }
  },
})

const apartmentFrom = boundModel(apartmentArea, apartmentLimits, 0)
const apartmentTo = boundModel(apartmentArea, apartmentLimits, 1)
const totalFrom = boundModel(totalArea, totalLimits, 0)
const totalTo = boundModel(totalArea, totalLimits, 1)

const sortRange = (target) => {
  const [from, to] = target.value
  if (from > to) {
    target.value = [to, from]
    updateValue()
  }
}

const sortApartment = () => sortRange(apartmentArea)
const sortTotal = () => sortRange(totalArea)

const updateValue = () => {
  emit('change', {
    apartment: apartmentArea.value,
    area: totalArea.value,
    unit: areaUnit.value,
  })
}

const unitChange = () => {
  totalArea.value = sliderLimits.value.pindala[areaUnit.value]
  updateValue()
}

const reset = () => {
  apartmentArea.value = deepCopy(sliderLimits.value.korteri_pind)
  totalArea.value = deepCopy(sliderLimits.value.pindala[SEARCH_DEFAULT_UNIT])
  areaUnit.value = SEARCH_DEFAULT_UNIT
  updateValue()
}
</script>

<style scoped>
:deep(.p-togglebutton-checked .p-togglebutton-content) {
  background: var(--p-primary-color) !important;
  color: var(--p-primary-contrast-color) !important;
}

:deep(.p-togglebutton.p-togglebutton-checked) {
  background: var(--p-primary-color) !important;
  border-color: var(--p-primary-color) !important;
}

:deep(.p-togglebutton-sm) {
  font-size: 0.75rem !important;
  line-height: 0.85rem !important;
}
</style>